/** Live presence board — beneficiaries currently in a call.
 *
 * Polls /v1/sessions/live (~15s). Each row links straight into the
 * session. Reusable: operator dashboard + campaign-detail overview.
 * Renders nothing without session.read.
 *
 * The sound + browser notification used to live here. They moved to
 * `CallAlertBanner` (2026-10-01), which is mounted once in AppLayout and
 * therefore fires on every screen rather than only the two this board
 * appears on. Leaving a copy here would have double-alerted on exactly
 * those two.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { useHasPermission } from "@/lib/permissions";
import { LIVE_QUERY_KEY } from "./liveSessions";

type LiveSession = {
  id: string;
  title: string;
  campaign_id: string | null;
  campaign_name: string;
  operator_id: number | null;
  operator_email: string;
  started_at: string | null;
  scheduled_for: string | null;
  beneficiary_name: string;
  field_joined_at: string | null;
};

type LiveResponse = { sessions: LiveSession[]; count: number };

async function fetchLiveSessions(campaignId?: string): Promise<LiveResponse> {
  const res = await api.get<LiveResponse>(ApiPaths.sessionsLive, {
    params: campaignId ? { campaign: campaignId } : undefined,
  });
  return res.data;
}

export function LiveInspectionsBoard({
  campaignId,
  className = "",
}: {
  campaignId?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const canSee = useHasPermission("session.read");
  const q = useQuery({
    // Same key as CallAlertBanner when unfiltered, so the two share one
    // request instead of polling the endpoint twice every 15s.
    queryKey: campaignId ? ["sessions-live", campaignId] : LIVE_QUERY_KEY,
    queryFn: () => fetchLiveSessions(campaignId),
    refetchInterval: 15000,
    enabled: canSee,
  });

  if (!canSee) return null;

  const sessions = q.data?.sessions ?? [];

  return (
    <div className={`card ${className}`}>
      <div className="mb-2 flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          {sessions.length > 0 && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-nb-mint opacity-70" />
          )}
          <span
            className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
              sessions.length > 0 ? "bg-nb-mint" : "bg-nb-text-3"
            }`}
          />
        </span>
        <h3 className="text-sm font-semibold text-nb-text">
          {t("sessions.live.title")}
        </h3>
        {sessions.length > 0 && (
          <span className="text-xs text-nb-text-2">({sessions.length})</span>
        )}
      </div>

      {sessions.length === 0 ? (
        <p className="text-xs text-nb-text-3">{t("sessions.live.empty")}</p>
      ) : (
        <ul className="divide-y divide-nb-border/60">
          {sessions.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-nb-text">
                  {s.beneficiary_name || t("sessions.live.aBeneficiary")}
                </div>
                <div className="truncate text-xs text-nb-text-2">
                  {t("sessions.live.inSession")}
                  {s.field_joined_at && (
                    <span className="text-nb-text-3">
                      {" "}
                      · {formatRelative(s.field_joined_at)}
                    </span>
                  )}
                  {s.campaign_name && (
                    <span className="text-nb-text-3"> · {s.campaign_name}</span>
                  )}
                </div>
              </div>
              <Link to={`/sessions/${s.id}`} className="btn-primary text-xs">
                {t("sessions.live.join")}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
